import type { CoworldSwitcherRow } from "@softmax/web-ux/shell";
import type { useRoute } from "../../navigation/use-route";
import {
  buildCoworldOptions,
  leaguesOfCoworld,
  matchDirectoryCoworlds,
  type PortfolioSeatLike,
  type CoworldTarget,
  type DirectoryEntryLike,
} from "./coworld-switcher-data";
import type { LeagueDirectoryEntry } from "./league-directory";
import { siteShape } from "../site-shape";
import { switchCoworldHref, switchLeagueHref } from "./switch-coworld-href";

export function switcherRows({
  seats,
  directory,
  current,
  route,
  query,
}: {
  readonly seats: PortfolioSeatLike[];
  readonly directory: LeagueDirectoryEntry[];
  readonly current: CoworldTarget | null;
  readonly route: ReturnType<typeof useRoute>;
  readonly query: string;
}): CoworldSwitcherRow[] {
  const byLeague = new Map(directory.map((entry) => [entry.league_id, entry]));
  const options = buildCoworldOptions({
    seats,
    targetOf: (leagueId) => byLeague.get(leagueId) ?? null,
    current,
  });
  const filtered = options.filter((option) =>
    option.name.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const directoryMatches = matchDirectoryCoworlds({
    directory,
    query,
    alreadyShown: new Set(filtered.map((option) => option.route.game.id)),
  });
  const currentLeagues =
    siteShape.leagueSwitching && current !== null && query.trim() === ""
      ? leaguesOfCoworld({ directory, gameSlug: current.route.game.slug })
      : [];
  const currentLeagueId = selectedLeagueId({ route, currentLeagues });

  const rows: CoworldSwitcherRow[] = [];

  for (const option of [...filtered, ...directoryMatches]) {
    const isCurrent = option.route.game.id === current?.route.game.id;
    rows.push({
      id: `coworld:${option.route.game.id}`,
      kind: "coworld",
      name: option.name,
      href: switchCoworldHref({ pathname: route.pathname, target: option }),
      current: isCurrent,
      member: option.member,
    });

    if (!isCurrent) continue;

    for (const league of currentLeagues)
      rows.push({
        id: `league:${league.league_id}`,
        kind: "league",
        name: league.name,
        href: switchLeagueHref({ pathname: route.pathname, target: league }),
        current: league.league_id === currentLeagueId,
      });
  }

  return rows;
}

function selectedLeagueId({
  route,
  currentLeagues,
}: {
  readonly route: ReturnType<typeof useRoute>;
  readonly currentLeagues: readonly DirectoryEntryLike[];
}): string | undefined {
  const namedLeague = route.area === "game" ? route.league : undefined;
  let currentLeagueId = namedLeague
    ? currentLeagues.find((league) => league.route.league === namedLeague)
        ?.league_id
    : undefined;

  if (
    !currentLeagueId &&
    (route.section === "play" || route.section === "preview")
  )
    currentLeagueId = currentLeagues.find(
      (league) => league.league_id === league.default_league_id,
    )?.league_id;

  return currentLeagueId;
}
